import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {sizes} from 'patient-ping-remedy/packages/theme';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Button from 'patient-ping-remedy/packages/button';
import {Menu, MenuItem, MenuItemTypography} from 'patient-ping-remedy/packages/menu';
import Popover from 'patient-ping-remedy/packages/popover';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAuthStore} from '@/store/auth_store';
import {hasJourneyEngineDetails, taskStatusDisplayValues, taskTypeDisplayValues} from '@/api/dto/care-journey';
import {MessageThreadSummary} from '@/api/dto/message';
import {SearchParam} from '@/helpers/search_params';
// CARECO-6031: disabled along with the care journey web socket subscription below.
// import {useWebSocketSubscription} from '@/hooks/useWebSocketSubscription';
import {usePageTitle, usePageHeaderContent} from '@/hooks/useBreadcrumbs';
import {useMixpanel} from '@/hooks/useMixpanel';
import {useFeatureToggles} from '@/hooks/useFeatureToggles';
import {
  DataPermissionLevel, DataPermissionLevelNew, DataSource,
  Feature, FeatureNew, FeaturePermissionLevel
} from '@/api/dto/permissions';
import {FeatureToggle, GroupType} from '@/api/dto/dto';
import {MixpanelEventType, MixpanelTaskEvent} from '@/api/dto/mixpanel';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import Helpers from '@/helpers/helpers';
import ClientDemographics from '@clients/profile_page/ClientDemographics';
import TabsComponent, {TabsRef} from '@common/TabsComponent';
import TaskEditProgress, {TaskEditProgressRef} from '@/components/pages/tasks/task_edit/progress/TaskEditProgress';
import TaskEditAttachments from '@/components/pages/tasks/task_edit/attachments/TaskEditAttachments';
import TaskEditLog from '@/components/pages/tasks/task_edit/log/TaskEditLog';
import RawTaskJson from '@/components/pages/tasks/task_edit/RawTaskJson';
import MessageThreadDrawer from '@clients/profile_page/messages_tab/MessageThreadDrawer';
import ConfirmCloseModal from '@/components/pages/tasks/task_edit/ConfirmCloseModal';
import Loading from '@common/Loading';
import {Content, Main, Messages, MessagesTitle, TabsWrapper} from '@/components/pages/tasks/task_detail/TaskDetailPage.styles';
import useJourneyRoster from '@/hooks/useJourneyRoster';

export default function TaskDetailPage() {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get(SearchParam.TASK_ID);
  const navigate = useNavigate();
  // CARECO-6031: webSocketApi is no longer pulled off the context here, see the disabled subscription below.
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const queryClient = useQueryClient();
  const tabsRef = useRef<TabsRef>(null);
  const taskEditProgressRef = useRef<TaskEditProgressRef>(null);
  const [newMessageThread, setNewMessageThread] = useState<MessageThreadSummary>();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();

  const {rosterId: effectiveRosterId} = useJourneyRoster();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  // CARECO-6031: only consumer was the web socket subscription disabled below. Left in place to
  // keep this rollback a comment-only diff.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canViewTaskForRoster = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.VIEW,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.VIEW,
      Feature.TASKS,
    ));

  const canActOnTaskListForRoster = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      Feature.TASKS,
    ));

  const isDemographicEditAvailable = !!currentUser &&
    !!currentRoster?.admittingFacility &&
    !currentRoster.automated &&
    currentRoster.type !== GroupType.CRISIS &&
    (isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserDataPermissionRosterNew(
        currentUser,
        currentRoster,
        DataPermissionLevelNew.ACT,
        DataSource.ADT,
      ) : PermissionsHelpers.canForUserDataPermissionRoster(
        currentUser,
        currentRoster,
        DataPermissionLevel.WRITE,
        DataSource.ADT,
      ));

  // CARECO-6031: disabled, not pending re-enablement. This subscription (added in CARECO-4955)
  // invalidated 'careJourneyDetails' on every care journey event arriving over the web socket. On
  // the Task detail page that refetch re-rendered TaskEditProgress underneath the user and wiped
  // anything typed into a journey form field that had not been committed yet; any action on the
  // same journey from another tab or user triggered it. CARECO-4955 was dev-initiated tech debt
  // with no product requirement behind it, so we are rolling back to the previous behaviour:
  // care journey details are fetched on page load only, and the user refreshes to see async
  // updates. Do not restore this without a mechanism that leaves in-progress form entry intact.
  // useWebSocketSubscription(
  //   () => {
  //     if (taskId && canViewTaskForRoster) {
  //       return webSocketApi?.subscribeCareJourneys(taskId, () => {
  //         void queryClient.invalidateQueries({queryKey: ['careJourneyDetails', taskId]});
  //       });
  //     }
  //   },
  //   [taskId],
  //   [canViewTaskForRoster],
  // );

  const {data: careJourneyDetails, isLoading, isFetching, error} = useQuery({
    queryKey: ['careJourneyDetails', taskId],
    queryFn: () => carecoApi?.getCareJourney(taskId!, effectiveRosterId!),
    enabled: !!carecoApi && !!taskId && !!effectiveRosterId,
  });

  const {data: messageThreadSummaries, isFetching: isFetchingMessages} = useQuery({
    queryKey: ['messageThreadSummaries', careJourneyDetails?.clientProfile.patient.patientId,
      careJourneyDetails?.careJourneyInstanceId],
    queryFn: () => carecoApi?.getMessageThreadSummaries(
      effectiveRosterId!,
      careJourneyDetails!.clientProfile.patient.patientId,
      careJourneyDetails!.careJourneyInstanceId,
      null,
      null
    ),
    enabled: !!carecoApi && !!careJourneyDetails && !!effectiveRosterId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const isBambooUser = !!currentUser && PermissionsHelpers.isBamboo(currentUser);

  const {data: additionalEvents} = useQuery({
    queryKey: ['additionalEvents', taskId],
    queryFn: () => carecoApi?.getAdditionalEvents(taskId!, effectiveRosterId!),
    enabled: !!carecoApi && !!taskId && !!effectiveRosterId && isBambooUser,
  });

  usePageTitle(careJourneyDetails && taskTypeDisplayValues[careJourneyDetails.eventType]);

  usePageHeaderContent(careJourneyDetails && (
    <Popover
      toggle={() => setShowPopover(!showPopover)}
      isOpen={showPopover}
      content={
        <Menu
          onHide={() => setShowPopover(false)}
          marginTop
          marginBottom
        >
          <MenuItem
            disabled={
              // Enabled for both OE (TCM) and CWE (FLOWABLE) journeys once their
              // engine details load; CWE close/cancel is handled server-side.
              !hasJourneyEngineDetails(careJourneyDetails)
              || careJourneyDetails?.userAssignment?.assignedToAi
              || !canActOnTaskListForRoster
            }
            onClick={() => {
              setShowConfirmClose(true);
              setShowPopover(false);
            }}
          >
            <MenuItemTypography>Close</MenuItemTypography>
          </MenuItem>
        </Menu>
      }
    >
      <Button onClick={() => {}} disabled={careJourneyDetails?.status?.toLowerCase() === 'closed'
          || careJourneyDetails?.status?.toLowerCase() === 'cancelled'}>
        {careJourneyDetails?.status ? taskStatusDisplayValues[careJourneyDetails.status] ?? '' : ''}
        <Icon
          iconClass={ICONS['angle-down']}
          iconSize="lg"
          weight="far"
          className={css({marginLeft: sizes.xxSmall})}
        />
      </Button>
    </Popover>
  ));

  const navigateBack = useCallback(() => navigate('/tasks'), [navigate]);

  const invalidate = useCallback(() => {
    void queryClient.refetchQueries({queryKey: ['careJourneyDetails', taskId]});
    void queryClient.removeQueries({queryKey: ['careJourneysData', effectiveRosterId]});
  }, [taskId, effectiveRosterId, queryClient]);

  useEffect(() => {
    if (error) {
      Helpers.alertOnError('Task details could not be loaded.', error);
      navigateBack();
    }
  }, [error]);

  const initialRosterId = useRef(effectiveRosterId);

  useEffect(() => {
    if (effectiveRosterId && initialRosterId.current && effectiveRosterId !== initialRosterId.current) {
      navigateBack();
    }
    initialRosterId.current = effectiveRosterId;
  }, [effectiveRosterId]);

  useEffect(() => {
    if (careJourneyDetails && currentRoster && mixpanelEventProperties) {
      const event: MixpanelTaskEvent = {
        eventName: MixpanelEventType.BIH_TASK_VIEWED,
        eventType: careJourneyDetails.eventType,
        careJourneyName: careJourneyDetails.journeyType,
        ...mixpanelEventProperties!,
        ...getCurrentGroupDetails(currentRoster)
      };
      carecoApi?.postMixpanelEvent(event);
    }
  }, [mixpanelEventProperties, careJourneyDetails, currentRoster]);

  const setCurrentTab = (index: number, stepName: string) => {
    tabsRef.current?.changeTab(index);
    setTimeout(() => taskEditProgressRef.current?.changeTab(careJourneyDetails?.steps
      .findIndex((step) => step.stepName === stepName) ?? -1));
  };

  if (!taskId) return <Navigate to="/tasks" replace />;

  if (isLoading) return <Loading />;

  const messagesSection = (
    <Messages>
      <MessagesTitle>Messages</MessagesTitle>
      {isFetchingMessages
        ? <Loading />
        : careJourneyDetails && (
          <MessageThreadDrawer
            patient={careJourneyDetails.clientProfile}
            careJourney={careJourneyDetails}
            messageThreadSummary={messageThreadSummaries?.messageThreadSummaries[0] ?? newMessageThread}
            simple
            onNewMessageThread={setNewMessageThread}
          />
        )}
    </Messages>
  );

  const withLayout = (content: React.ReactNode) => (
    <Main>{content}</Main>
  );

  return (
    <>
      {careJourneyDetails && (
        <ClientDemographics
          patient={careJourneyDetails.clientProfile}
          isExpandable
          isElevated
          isDemographicsEditable={isDemographicEditAvailable}
          linkName
        />
      )}
      {careJourneyDetails && (
        <Content>
          <TabsWrapper>
            <TabsComponent
              ref={tabsRef}
              tabsClassName={css({paddingLeft: `${sizes.medium} !important`})}
              tabs={[
                {
                  value: 0,
                  label: 'Progress',
                  content: withLayout(
                    <TaskEditProgress
                      ref={taskEditProgressRef}
                      fetching={isFetching}
                      careJourneyDetails={careJourneyDetails}
                      invalidate={invalidate}
                      disabled={!canActOnTaskListForRoster}
                    />
                  ),
                },
                {
                  value: 1,
                  label: 'Attachments',
                  content: withLayout(
                    <TaskEditAttachments careJourneyDetails={careJourneyDetails} />
                  ),
                },
                {
                  value: 2,
                  label: 'Log',
                  content: withLayout(
                    <TaskEditLog careJourneyDetails={careJourneyDetails} setCurrentTab={setCurrentTab} />
                  ),
                },
                {
                  value: 3,
                  label: 'Raw JSON',
                  content: <RawTaskJson
                    careJourneyDetails={careJourneyDetails}
                    additionalEvents={additionalEvents}
                  />,
                  hidden: !isBambooUser
                },
              ]}
            />
          </TabsWrapper>
          {messagesSection}
        </Content>
      )}
      {showConfirmClose && (
        <ConfirmCloseModal
          close={() => setShowConfirmClose(false)}
          careJourneyDetails={careJourneyDetails!}
          closeTaskEditDrawer={navigateBack}
          invalidate={invalidate}
        />
      )}
    </>
  );
}
